import { Add, Button, FormCard, Modal, Select } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import GoalsList from 'screens/Residents/screens/ResidentsGoalsScreen/components/GoalsList/GoalsList'
import useGoalsIDs from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoalsIDs'
import styled from 'styled-components'

type CarePlanGoalsListProps = {
  compactSpacing?: boolean
  disabled: boolean
  onChange?: (value: string[]) => void
  value: string[]
}

const CarePlanGoalsListInput: FC<CarePlanGoalsListProps> = ({
  compactSpacing = false,
  disabled = true,
  value,
  onChange
}) => {
  const { t } = useTranslation(NAMESPACE.GOALS)

  const { id: residentID } = useParams()
  const { filteredGoals, goals } = useGoalsIDs({
    residentID,
    goalsIDs: value
  })

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedItems, setSelectedItems] = useState(value)

  const addHandler = () => {
    setIsModalVisible(true)
  }
  const confirmHandler = () => {
    onChange?.(selectedItems)

    setIsModalVisible(false)
  }

  const onSelectChange = (
    options: { label: React.ReactNode; value: string }[]
  ) => {
    const newSelectedValues = options.map((option) => option.value)

    setSelectedItems(newSelectedValues)
  }
  const unlinkHandler = (id: string) => {
    const newValue = value.filter((element) => element !== id)

    onChange?.(newValue)
  }

  const options =
    goals?.map((goal) => ({
      label: goal.title,
      value: goal.id
    })) || []

  const hasGoals = !!filteredGoals?.length

  return (
    <Wrapper $compactSpacing={compactSpacing}>
      <Modal
        isModalVisible={isModalVisible}
        localisation={{
          title: t(goalsConstants.SELECT_FROM_AVAILABLE_GOALS),
          confirmText: t(goalsConstants.CONFIRM),
          cancelText: t(goalsConstants.CANCEL)
        }}
        noPadding
        onCancel={() => setIsModalVisible(false)}
        onConfirm={() => confirmHandler()}
      >
        <Select<string[], { label?: React.ReactNode; value: string }>
          defaultValue={value}
          mode="multiple"
          onChange={(value, options) => onSelectChange(options as [])}
          options={options}
          placeholder={t(goalsConstants.GOALS)}
          style={{ width: '100%' }}
        />
      </Modal>
      <FormCard
        title={t('components.logEditForm.linkedGoals', {
          ns: NAMESPACE.RESOURCES
        })}
        rightChildren={
          !disabled && (
            <Button
              disabled={disabled}
              icon={<Add />}
              onClick={() => addHandler()}
              type="button"
              variant="text"
            >
              {t(goalsConstants.LINK_A_GOAL)}
            </Button>
          )
        }
      >
        {hasGoals ? (
          <GoalsListStyled
            disabled={disabled}
            hasBorder
            items={filteredGoals || []}
            onUnlink={unlinkHandler}
            residentID={residentID}
          />
        ) : null}
      </FormCard>
    </Wrapper>
  )
}

const Wrapper = styled.div<{ $compactSpacing: boolean }>`
  margin-top: ${({ $compactSpacing }) => ($compactSpacing ? 0 : 30)}px;
`

const GoalsListStyled = styled(GoalsList)`
  > h4 {
    opacity: 0.7;
    color: rgb(51, 51, 51);
    font-weight: 700;
  }
`

export default CarePlanGoalsListInput
